import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppKit } from "@reown/appkit/react";
import { useAccount, useDisconnect, useSwitchChain } from "wagmi";

import { ACTIVE_CHAIN } from "@/config/chain";
import { formatAddress } from "@/lib/format";
import { HistoryModal } from "./history-modal";

const IconClock = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.1" />
    <path d="M6.5 3.5v3l2 1.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconPower = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <path d="M6.5 2v4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    <path d="M4 4.1A4.5 4.5 0 1 0 9 4.1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const baseClass =
  "px-3 md:px-5 h-9 rounded-[2px] text-[11px] md:text-[12px] tracking-[0.08em] md:tracking-[0.14em] uppercase font-sans whitespace-nowrap transition-all border";

const Pill = ({
  children,
  onClick,
  variant = "primary",
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost" | "warn";
  title?: string;
}) => {
  const styles =
    variant === "primary"
      ? { background: "var(--ink-1)", color: "var(--on-light)", borderColor: "var(--ink-1)" }
      : variant === "warn"
      ? { background: "transparent", color: "var(--accent-rose)", borderColor: "var(--accent-rose)" }
      : { background: "transparent", color: "var(--ink-2)", borderColor: "var(--line-2)" };
  return (
    <button
      onClick={onClick}
      title={title}
      className={`${baseClass} hover:opacity-90`}
      style={{ ...styles, fontWeight: 500 }}
    >
      {children}
    </button>
  );
};

export const ConnectButton = () => {
  const { open } = useAppKit();
  const { address, chainId, status } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!dropdownOpen) return;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !wrapperRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  if (status === "connecting" || status === "reconnecting") {
    return <Pill variant="ghost">Connecting…</Pill>;
  }

  if (status === "connected" && address) {
    const wrongChain = chainId !== ACTIVE_CHAIN.id;
    if (wrongChain) {
      return (
        <Pill
          variant="warn"
          onClick={() => switchChain({ chainId: ACTIVE_CHAIN.id })}
        >
          Switch to Base
        </Pill>
      );
    }
    return (
      <>
        <div className="relative" ref={wrapperRef}>
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            className={`${baseClass} hover:opacity-90 tabular`}
            style={{
              background: "transparent",
              color: "var(--ink-1)",
              borderColor: "var(--line-3)",
              fontWeight: 400,
            }}
          >
            <span
              className="inline-block w-[6px] h-[6px] rounded-full mr-3"
              style={{ background: "var(--accent-jade)" }}
            />
            {formatAddress(address)}
          </button>

          {dropdownOpen && createPortal(
            <div
              ref={panelRef}
              className="rounded-[var(--radius-md)] overflow-hidden"
              style={{
                position: "fixed",
                top: dropdownPos.top,
                right: dropdownPos.right,
                background: "#252525",
                border: "1px solid var(--line-2)",
                minWidth: "10rem",
                zIndex: 9999,
              }}
            >
              <button
                type="button"
                className="w-full text-left flex items-center gap-2.5 px-4 py-3 font-mono text-[11px] tracking-[0.10em] uppercase transition-opacity hover:opacity-70"
                style={{ color: "var(--ink-2)" }}
                onClick={() => {
                  setDropdownOpen(false);
                  setHistoryOpen(true);
                }}
              >
                <IconClock />
                History
              </button>
              <button
                type="button"
                className="w-full text-left flex items-center gap-2.5 px-4 py-3 font-mono text-[11px] tracking-[0.10em] uppercase transition-opacity hover:opacity-70"
                style={{ color: "var(--accent-rose)", borderTop: "1px solid var(--line-1)" }}
                onClick={() => {
                  setDropdownOpen(false);
                  disconnect();
                }}
              >
                <IconPower />
                Disconnect
              </button>
            </div>,
            document.body,
          )}
        </div>

        {historyOpen && <HistoryModal onClose={() => setHistoryOpen(false)} />}
      </>
    );
  }

  return (
    <Pill onClick={() => open()}>
      Connect<span className="hidden sm:inline"> Wallet</span>
    </Pill>
  );
};
